/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useEffect, useState } from 'react';

import styled from 'styled-components';

import { AgentsCreationSuccessModal } from './AgentsCreationSuccessModal.container';

import {
  Banner,
  BannerHeading,
  BannerIcon,
  BannerSuffix,
  Box,
  ToastButton,
  Typography,
  Spacer,
} from '@aircall/tractor-v2';
import { AgentRolesPicker } from '@components/AgentRolesPicker/AgentRolesPicker';
import { GridItem, GridLayout } from '@components/GridLayoutV2';
import { RouterLink } from '@components/Link';
import { ModalForm } from '@components/ModalFormV2';
import { TextField } from '@components/TextField/TextField';
import {
  Gap,
  GenericFormError,
  generateUniqId,
  validateRequired,
  validateEmail,
} from '@dashboard/library';
import { GetAllRolesQuery_getAllRoles } from '@generated/GetAllRolesQuery';
import { useCompanyBillingDetails } from '@hooks/useCompanyBillingDetails/useCompanyBillingDetails';
import { useComputeRolesInitialFormValues } from '@hooks/useComputeRolesInitialFormValues/useComputeRolesInitialFormValues';
import { useEntitiesCount } from '@hooks/useEntitiesCount/useEntitiesCount';
import { useFeatures } from '@hooks/useFeatures/useFeatures';
import { useFromOnboarding } from '@hooks/useFromOnboarding';
import { useInviteAgent } from '@hooks/useInviteAgent';
import { useReseller } from '@hooks/useReseller/useReseller';
import { useToggle } from '@hooks/useToggle/useToggle';
import { useTracker } from '@hooks/useTracker/useTracker';
import { FormApi } from 'final-form';
import { Trans, useTranslation } from 'react-i18next';

export enum USER_ROLES {
  USER = 'user',
  ADMIN = 'admin',
}

export interface CreateAgentFormValues {
  email: string;
  firstName: string;
  lastName: string;
  roles: GetAllRolesQuery_getAllRoles[];
  confirmed: boolean;
}

export interface AgentsCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreationCompleted?: () => void;
  onCreationSubmited?: (id?: string) => void;
}

type Submitfunction = (
  values: CreateAgentFormValues,
  form: FormApi<CreateAgentFormValues, CreateAgentFormValues>
) => Promise<Record<string, string> | GenericFormError | undefined>;

type UseConfirmSubmitParams = {
  handleSubmit: Submitfunction;
  active: boolean;
};

function useConfirmSubmit({ handleSubmit, active }: UseConfirmSubmitParams): {
  onSubmit: Submitfunction;
} {
  const onSubmit = useCallback(
    async (input, form) => {
      if (active && !input.confirmed) {
        form.change('confirmed', true);
        return { confirmed: '' };
      }
      const { confirmed: _, ...values } = input;
      return handleSubmit(values, form);
    },
    [handleSubmit, active]
  );

  return {
    onSubmit,
  };
}

const Hide = styled(Box)<{ hide: boolean }>`
  display: ${({ hide }) => (hide ? 'none' : 'default')};
`;

export function UserPriceWarning({ dataTest }: { dataTest: string }) {
  const { t } = useTranslation();
  const [isBannerVisible, setBannerVisibility] = useState(true);
  const { totalUsers } = useEntitiesCount();
  const {
    companyBillingDetails: { subscription },
  } = useCompanyBillingDetails();
  const { isReseller } = useReseller();
  const { dashboardAgentsCreationDisplayNewMaxWarning } = useFeatures();

  let warn = '';

  if (!dashboardAgentsCreationDisplayNewMaxWarning) {
    warn = 'warn';
  } else if (subscription) {
    const totalIncluded =
      (subscription?.totalIncludedUsers ?? 0) + (subscription?.maxAdditionalUsers ?? 0);
    if (totalUsers >= totalIncluded) {
      warn = isReseller ? 'additional_charges_reseller' : 'additional_charges';
    } else {
      warn = 'no_charges';
    }
  }
  if (!warn) {
    return null;
  }

  return isBannerVisible ? (
    <Banner data-test={dataTest} variant='info' inline>
      <BannerIcon />
      <BannerHeading>
        <Typography whiteSpace='pre-line'>
          <Trans
            values={{
              price: subscription?.pricePerAdditionalUser,
              currency: subscription?.currency,
              billingFrequency: subscription?.billingFrequencyAddUserNumber,
            }}
            i18nKey={`users.creation.${warn}`}
          >
            <RouterLink to='/account/plan'>plan</RouterLink>
            <RouterLink to='/account/billing'>billing</RouterLink>
          </Trans>
        </Typography>
      </BannerHeading>
      <BannerSuffix>
        <ToastButton
          data-test={`${dataTest}-dismiss-button`}
          onClick={() => {
            setBannerVisibility(false);
          }}
        >
          {t('generic.dismiss')}
        </ToastButton>
      </BannerSuffix>
    </Banner>
  ) : null;
}

export function AgentsCreationModal({
  isOpen,
  onClose,
  onCreationCompleted,
  onCreationSubmited,
}: AgentsCreationModalProps) {
  const [isSuccessModalOpen, _, setSuccessModalOpen, setSuccessModalClosed] = useToggle();
  const [newlyCreatedUser, setNewlyCreatedUser] = useState<{
    firstName: string;
    lastName: string;
    roles: GetAllRolesQuery_getAllRoles[];
  } | null>(null);
  const { t } = useTranslation();
  const { track } = useTracker();
  const isFromOnboarding = useFromOnboarding();
  const initialRoles = useComputeRolesInitialFormValues();
  const [funnelId, setFunnelId] = useState(generateUniqId());
  const { totalUsers } = useEntitiesCount();
  const {
    companyBillingDetails: { subscription },
  } = useCompanyBillingDetails();
  let displayAdditionalChargeWarning = false;
  const { dashboardAgentsCreationDisplayNewMaxWarning } = useFeatures();

  if (subscription && dashboardAgentsCreationDisplayNewMaxWarning) {
    const totalIncluded = subscription?.totalIncludedUsers ?? 0;
    if (totalUsers >= totalIncluded) {
      displayAdditionalChargeWarning = true;
    }
  }

  const onCompleted = useCallback(
    (id?: string) => {
      onCreationSubmited?.(id);

      // hide creation modal and open success modal
      onClose();
      setSuccessModalOpen();
    },
    [onClose, onCreationSubmited, setSuccessModalOpen]
  );

  const { inviteAgent } = useInviteAgent({
    onCompleted,
    funnelId,
  });

  useEffect(() => {
    if (isOpen) {
      track({
        event: 'user_creation_funnel_started',
        payload: {
          is_part_of_onboarding: isFromOnboarding,
          funnel_id: funnelId,
        },
      });
    }
  }, [funnelId, isFromOnboarding, isOpen, track]);

  // This is done in a separate useEffect otherwise having funnelId in deps and setting it will create infinite loop
  useEffect(() => {
    if (!isOpen) {
      setFunnelId(generateUniqId());
    }
  }, [isOpen]);

  function handleSubmit(input: CreateAgentFormValues) {
    setNewlyCreatedUser({
      firstName: input.firstName,
      lastName: input.lastName,
      roles: input.roles,
    });

    return inviteAgent(input);
  }

  const initialValues: CreateAgentFormValues = {
    firstName: '',
    lastName: '',
    roles: initialRoles,
    email: '',
    confirmed: false,
  };
  const { onSubmit } = useConfirmSubmit({
    handleSubmit,
    active: displayAdditionalChargeWarning,
  });

  return (
    <>
      <ModalForm<CreateAgentFormValues>
        title={t('users.creation.title')}
        formProps={{
          onSubmit,
          initialValues,
          keepDirtyOnReinitialize: true,
        }}
        show={isOpen}
        allowPristineSubmit
        onHide={onClose}
        data-tracking='user-creation-process-completed'
        size='large'
      >
        {({ submitting, values }) => (
          <Gap gap='l' p='xxs' flexDirection='column' data-test='create-agent-form'>
            <UserPriceWarning dataTest='form-warning' />
            <Hide overflowY='auto' maxH={500} data-test='hide-layout' hide={values.confirmed}>
              <GridLayout columnGap={0}>
                <GridItem xs={6}>
                  <Spacer direction='vertical' space={10} fluid pr='m'>
                    <Typography variant='bodySemiboldM' fontWeight='600'>
                      {t('users.creation.modal_section_info_title')}
                    </Typography>
                    <Typography variant='bodyMediumS' fontWeight='500' pb={2}>
                      {t('users.creation.modal_section_info_description')}
                    </Typography>
                    {/* auto focus on firstname field when Form renders */}
                    <TextField
                      autoFocus
                      name='firstName'
                      label={t('app.form.first_name.label')}
                      validate={validateRequired}
                      data-test='firstname-input'
                      value={values.firstName}
                    />
                    <TextField
                      name='lastName'
                      label={t('app.form.last_name.label')}
                      validate={validateRequired}
                      data-test='lastname-input'
                      value={values.lastName}
                    />
                    <TextField
                      name='email'
                      label={t('app.form.email.label')}
                      validate={validateEmail}
                      data-test='email-input'
                      value={values.email}
                    />
                  </Spacer>
                </GridItem>

                <GridItem xs={6} borderLeft='1px solid' borderColor='neutral-200'>
                  <Spacer direction='vertical' space={10} fluid pl='m'>
                    <Typography variant='bodySemiboldM' fontWeight='600'>
                      {t('users.creation.modal_section_role_title')}
                    </Typography>
                    <Typography variant='bodyMediumS' fontWeight='500' pb={2}>
                      {t('users.creation.modal_section_role_description')}
                    </Typography>
                    <AgentRolesPicker disabled={submitting} name='roles' />
                  </Spacer>
                </GridItem>
              </GridLayout>
            </Hide>
          </Gap>
        )}
      </ModalForm>
      <AgentsCreationSuccessModal
        isModalOpen={isSuccessModalOpen}
        roles={newlyCreatedUser?.roles}
        onClose={() => {
          setSuccessModalClosed();
          onCreationCompleted?.();
        }}
        firstName={newlyCreatedUser?.firstName}
        lastName={newlyCreatedUser?.lastName}
      />
    </>
  );
}
